/* =============================================================================
   The sample dataset, in the reader's language
   -----------------------------------------------------------------------------
   The fixtures are sample DATA — the API will send its own records one day — but
   a demo screen is read by a person, and a Portuguese reader shown "Framing and
   roofing" beside "Em andamento" is reading a half-translated page. The vistoria
   vocabulary and the record content are therefore translated too.

   The English fixtures are the source of truth, and nothing here duplicates
   them. A record's own words are the fallback, so an entry nobody wrote falls
   through to English rather than to a blank or to a raw key.

   WHY A HOOK AND NOT A CONSTANT

   `domain.ts` is a module: its values are resolved once, before any provider
   exists, so a sentence stored in them is frozen in whatever language the module
   happened to load in and no language switch can ever reach it. Every screen
   therefore reads the fixtures through a hook, which resolves at RENDER time
   against the active catalogue, and re-resolves when the provider's locale
   changes.

   THE KEYS

   `<fixture>` in `fixtures.pt-BR.ts` is keyed by each record's STABLE
   identifier — the `id` the fixture already carries — and never by its position
   in an array nor by its English text: a key made of the words would be orphaned
   by the first copy edit, and a key made of an index silently re-points when a
   record is inserted above it.

   The two exceptions are honest ones. A closed enum in the fixture (a supplier's
   trade, an expense's category) has no id of its own, so its VALUE is the
   identifier — the value is the code, and the code is data. And a caption's
   subject is named in `domain.ts`, because captions repeat across developments
   and only a name survives a reordering.

   English is untouched by all of this: under `en` the catalogue has no entry for
   these keys, the lookup returns the key, and the guard below hands back the
   fixture's own English string — byte-identical, by construction rather than by
   review.
   ============================================================================= */

import { useCallback, useMemo } from 'react';
import {
  useTranslation,
  type NotificationItem,
  type ProjectUpdate,
  type SchedulePhase,
} from 'escrowbuild-ui';

import {
  DOCUMENTS,
  DRAW_LABELS,
  EVIDENCE,
  EXPENSES,
  MILESTONES,
  NOTIFICATIONS,
  OPPORTUNITIES,
  PHASE_SEQUENCE,
  PUBLICATIONS,
  REPORTS,
  SCHEDULES,
  UPDATES,
  publishedDocuments,
  publishedEvidence,
  publishedPublications,
  type DocumentRecord,
  type EvidenceItem,
  type Expense,
  type Milestone,
  type Opportunity,
  type Publication,
  type ReportRecord,
} from '@/data/domain';

/**
 * Resolves one fixture string: the catalogue for the active locale, or the
 * English the fixture itself carries.
 *
 * The `key` rung of the lookup is what `t` returns for an entry that does not
 * exist, so a miss is detectable without a second API — and it is the only miss
 * that can happen: under `en` there is no entry for ANY of these keys, which is
 * exactly how English stays byte-identical.
 */
type FixtureTranslate = (key: string, english: string) => string;

/** The English name of a phase, by the id every record carries. */
const PHASE_NAME_BY_ID = new Map(PHASE_SEQUENCE.map((phase) => [phase.id, phase.name]));

/**
 * The same vocabulary reached from the value, for the two fixtures that store a
 * phase as its NAME rather than as its id: the schedule rows and the suppliers.
 * Both carry one of the nine names above, so the mapping is exact rather than a
 * guess — and a name this map has never seen falls through to itself.
 */
const PHASE_ID_BY_NAME = new Map(PHASE_SEQUENCE.map((phase) => [phase.name, phase.id]));

/**
 * A closed enum value as a key: `'In progress'` becomes `in-progress`.
 *
 * Used only for the fixtures' own fixed vocabularies — a supplier's trade, an
 * expense's category, a contract's state — where the value IS the identifier.
 * Never for prose, which is keyed by an id instead.
 */
function enumKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** The translator these screens use, plus the locale it resolves against. */
function useFixtureT(): FixtureTranslate {
  const { t } = useTranslation();
  return useCallback<FixtureTranslate>(
    (key, english) => {
      const value = t(key);
      return value === key ? english : value;
    },
    [t],
  );
}

/* -----------------------------------------------------------------------------
   The fixture collections
   -----------------------------------------------------------------------------
   Each hook returns the fixture's own shape with only its text fields resolved,
   so every figure, every id and every flag is the one `domain.ts` derives. The
   arithmetic the screens do over these rows — the sums, the filters, the joins —
   is therefore untouched by translation.

   `useMemo` on `tf` is the identity contract: `tf` is stable for the life of a
   locale, so a screen memoising a table model on the array it receives keeps one
   model per language rather than one per render.
   -------------------------------------------------------------------------- */

export function useScheduleOf(projectId: string): SchedulePhase[] {
  const tf = useFixtureT();
  return useMemo(
    () =>
      (SCHEDULES[projectId] ?? []).map((phase) => ({
        ...phase,
        name: tf(`fixture.phase.${phase.id}`, phase.name),
      })),
    [projectId, tf],
  );
}

export function useMilestones(projectId?: string): Milestone[] {
  const tf = useFixtureT();
  return useMemo(
    () =>
      MILESTONES.filter((milestone) => !projectId || milestone.projectId === projectId).map(
        (milestone) => ({
          ...milestone,
          title: tf(`fixture.milestone.${milestone.id}`, milestone.title),
        }),
      ),
    [projectId, tf],
  );
}

export function usePublications(projectId?: string): Publication[] {
  const tf = useFixtureT();
  return useMemo(
    () => localizePublications(PUBLICATIONS, tf, projectId),
    [projectId, tf],
  );
}

/** Published only — the gate every investor-facing screen must go through. */
export function usePublishedPublications(projectId?: string): Publication[] {
  const tf = useFixtureT();
  return useMemo(
    () => localizePublications(publishedPublications(), tf, projectId),
    [projectId, tf],
  );
}

function localizePublications(
  source: Publication[],
  tf: FixtureTranslate,
  projectId?: string,
): Publication[] {
  return source
    .filter((publication) => !projectId || publication.projectId === projectId)
    .map((publication) => ({
      ...publication,
      title: tf(`fixture.publication.${publication.id}.title`, publication.title),
      body: tf(`fixture.publication.${publication.id}.body`, publication.body),
      period: tf(`fixture.publication.${publication.id}.period`, publication.period),
    }));
}

export function useEvidence(projectId?: string): EvidenceItem[] {
  const tf = useFixtureT();
  return useMemo(
    () => localizeEvidence(EVIDENCE, tf, projectId),
    [projectId, tf],
  );
}

export function usePublishedEvidence(projectId?: string): EvidenceItem[] {
  const tf = useFixtureT();
  return useMemo(
    () => localizeEvidence(publishedEvidence(), tf, projectId),
    [projectId, tf],
  );
}

function localizeEvidence(
  source: EvidenceItem[],
  tf: FixtureTranslate,
  projectId?: string,
): EvidenceItem[] {
  return source
    .filter((item) => !projectId || item.projectId === projectId)
    .map((item) => ({
      ...item,
      caption: tf(`fixture.evidence.${item.captionId}`, item.caption),
    }));
}

export function useDocuments(projectId?: string): DocumentRecord[] {
  const tf = useFixtureT();
  return useMemo(
    () => localizeDocuments(DOCUMENTS, tf, projectId),
    [projectId, tf],
  );
}

export function usePublishedDocuments(projectId?: string): DocumentRecord[] {
  const tf = useFixtureT();
  return useMemo(
    () => localizeDocuments(publishedDocuments(), tf, projectId),
    [projectId, tf],
  );
}

function localizeDocuments(
  source: DocumentRecord[],
  tf: FixtureTranslate,
  projectId?: string,
): DocumentRecord[] {
  return source
    .filter((document) => !projectId || document.projectId === projectId)
    .map((document) => ({
      ...document,
      name: tf(`fixture.document.${document.id}`, document.name),
    }));
}

export function useReports(): ReportRecord[] {
  const tf = useFixtureT();
  return useMemo(
    () =>
      REPORTS.map((report) => ({
        ...report,
        title: tf(`fixture.report.${report.id}.title`, report.title),
        period: tf(`fixture.report.${report.id}.period`, report.period),
        scope: tf(`fixture.report.${report.id}.scope`, report.scope),
      })),
    [tf],
  );
}

export function useOpportunities(): Opportunity[] {
  const tf = useFixtureT();
  return useMemo(
    () =>
      OPPORTUNITIES.map((opportunity) => ({
        ...opportunity,
        note: tf(`fixture.opportunity.${opportunity.id}`, opportunity.note),
      })),
    [tf],
  );
}

export function useExpenses(projectId?: string): Expense[] {
  const tf = useFixtureT();
  return useMemo(
    () =>
      EXPENSES.filter((expense) => !projectId || expense.projectId === projectId).map(
        (expense) => ({
          ...expense,
          /*
             The sentence is rebuilt rather than looked up: half of it is the
             phase dictionary and the other half is one of five draw labels, so
             storing the whole sentence per row would put 59 copies of the same
             eleven words in the catalogue, and the one that fell behind would be
             the one nobody looked at.
          */
          description: `${tf(
            `fixture.phase.${expense.phaseId}`,
            PHASE_NAME_BY_ID.get(expense.phaseId) ?? expense.phaseId,
          )} — ${tf(
            `fixture.expenseDraw.${expense.draw}`,
            DRAW_LABELS[expense.draw],
          )}`,
        }),
      ),
    [projectId, tf],
  );
}

export function useNotifications(): NotificationItem[] {
  const tf = useFixtureT();
  return useMemo(
    () =>
      NOTIFICATIONS.map((entry) => ({
        ...entry,
        title: tf(`fixture.notification.${entry.id}.title`, entry.title),
        description: tf(`fixture.notification.${entry.id}.description`, entry.description),
      })),
    [tf],
  );
}

export function useUpdates(): ProjectUpdate[] {
  const tf = useFixtureT();
  return useMemo(
    () =>
      UPDATES.map((entry) => ({
        ...entry,
        title: tf(`fixture.update.${entry.id}.title`, entry.title),
        body: tf(`fixture.update.${entry.id}.body`, entry.body),
      })),
    [tf],
  );
}

/* -----------------------------------------------------------------------------
   The closed vocabularies
   -----------------------------------------------------------------------------
   A supplier's trade and contract state, an expense's category and a person's
   role are fixed enumerations rather than prose, so they are resolved by value
   rather than by an id. A value no catalogue has an entry for comes back as
   itself: an unrecognised word in a cell is a visible defect with an obvious
   cause, where a blank or a key is a bug report about the layout.
   -------------------------------------------------------------------------- */

export interface FixtureVocabulary {
  /**
   * A phase, from either identifier it is stored under: the nine names are the
   * same vocabulary as the nine ids, so a caller that holds a schedule row's
   * `name` and a caller that holds an expense's `phaseId` both land here.
   */
  phase: (value: string) => string;
  trade: (value: string) => string;
  contractorStatus: (value: string) => string;
  expenseCategory: (value: string) => string;
  personRole: (value: string) => string;
}

export function useFixtureVocabulary(): FixtureVocabulary {
  const tf = useFixtureT();
  return useMemo(
    () => ({
      phase: (value: string) =>
        tf(
          `fixture.phase.${PHASE_ID_BY_NAME.get(value) ?? value}`,
          PHASE_NAME_BY_ID.get(value) ?? value,
        ),
      trade: (value: string) => tf(`fixture.trade.${enumKey(value)}`, value),
      contractorStatus: (value: string) => tf(`fixture.contractorStatus.${enumKey(value)}`, value),
      expenseCategory: (value: string) => tf(`fixture.expenseCategory.${enumKey(value)}`, value),
      personRole: (value: string) => tf(`fixture.personRole.${enumKey(value)}`, value),
    }),
    [tf],
  );
}

